// User Group List
// Display list of user groups

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Edit, Trash2, Users } from 'lucide-react';
import { useUserGroupMutations } from '../../../hooks/useUserGroups';
import { useGroupTypes, useShiftTypes, useWorkloadBalancingMethods } from '../../../hooks/useTaskNomenclatures';
import './UserGroupList.css';

const UserGroupList = ({ groups, onEdit, onRefresh, userId }) => {
  const { t } = useTranslation('userManagement');
  const { deleteGroup } = useUserGroupMutations();
  const { items: groupTypes } = useGroupTypes();
  const { items: shiftTypes } = useShiftTypes();
  const { items: balancingMethods } = useWorkloadBalancingMethods();

  // Helper functions to get human-readable names
  const getGroupTypeName = (groupTypeId) => {
    if (!groupTypeId) return t('notSet');
    const groupType = groupTypes?.find(type => type.id === groupTypeId);
    return groupType?.name || groupTypeId;
  };

  const getShiftTypeName = (shiftTypeId) => {
    if (!shiftTypeId) return t('allShifts');
    const shiftType = shiftTypes?.find(type => type.id === shiftTypeId);
    return shiftType?.name || shiftTypeId;
  };

  const getBalancingMethodName = (balancingId) => {
    if (!balancingId) return 'round_robin';
    const balancing = balancingMethods?.find(method => method.id === balancingId);
    return balancing?.name || balancingId;
  };

  const handleDelete = async (groupId, groupName) => {
    if (!window.confirm(`Are you sure you want to delete the group "${groupName}"?`)) {
      return;
    }

    try {
      await deleteGroup(groupId, userId);
      onRefresh();
    } catch (error) {
      alert(`Error deleting group: ${error.message}`);
    }
  };

  if (groups.length === 0) {
    return (
      <div className="empty-state">
        <Users size={48} color="#9ca3af" />
        <p>{t('noGroupsFound')}</p>
        <p className="empty-hint">{t('createFirstGroupHint')}</p>
      </div>
    );
  }

  return (
    <div className="group-list-container">
      <table className="group-table">
        <thead>
          <tr>
            <th>{t('groupName')}</th>
            <th>{t('groupDescription')}</th>
            <th>{t('groupType')}</th>
            <th>{t('groupDepartment')}</th>
            <th>{t('groupShift')}</th>
            <th>{t('maxTasks')}</th>
            <th>{t('balancing')}</th>
            <th>{t('actions')}</th>
          </tr>
        </thead>
        <tbody>
          {groups.map((group, index) => (
            <tr key={group.id ?? `group-${index}`} className="group-row">
              <td className="group-name-cell">
                <div className="group-name">{group.name}</div>
              </td>
              <td className="group-description-cell">
                <div className="group-description">{group.description || t('noDescription')}</div>
              </td>
              <td className="group-type-cell">
                <span className="group-type">{getGroupTypeName(group.groupType)}</span>
              </td>
              <td className="group-department-cell">
                <span className="group-department">{group.departmentName || 'All'}</span>
              </td>
              <td className="group-shift-cell">
                <span className="group-shift">{getShiftTypeName(group.shiftType)}</span>
              </td>
              <td className="group-max-tasks-cell">
                <span className="group-max-tasks">{group.assignmentRules?.maxConcurrentTasks || 3}</span>
              </td>
              <td className="group-balancing-cell">
                <span className="group-balancing">{getBalancingMethodName(group.assignmentRules?.workloadBalancing)}</span>
              </td>
              <td className="group-actions-cell">
                <div className="action-buttons">
                  <button
                    className="btn btn-edit btn-icon"
                    onClick={() => onEdit(group)}
                    title={t('editGroup')}
                  >
                    <Edit size={16} />
                  </button>
                  <button
                    className="btn btn-delete btn-icon"
                    onClick={() => handleDelete(group.id, group.name)}
                    title={t('deleteGroup')}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default UserGroupList;

